import {
  BaseWebComponentController,
  IListenerConfig,
  WebComponentEvent,
} from '../../../framework/BaseWebComponentController';
import { TasksWebComponent } from '../component/TasksWebComponent';
import { Dependencies, TaskItem, TasksGridEvents, getTaskTypeLabel, TaskStatus, RetryTasks, TaskSelectionStatus, GridSelectionStatuses } from '../types';
import { TasksRepository } from '../repository/TasksRepository';
import { TasksView } from '../view/TasksView';
import { templateTasks } from '../view/template';
import { GridEvents } from '../../../grid/types/common';
import { DataProviderType } from '../../../utils/request';
import { ReportRecurrenceType, ReportScheduleType } from '@nirvana/shared/constants';
import { GzDialogWebComponent } from '../../../ui/gzDialog';
import { showErrorToast, showSuccessToast } from '../../../ui/gzToastNotification';

export class TasksController extends BaseWebComponentController {
  public component: TasksWebComponent;
  public dataRepository: TasksRepository;
  public view: TasksView;
  public viewConstructor: typeof TasksView;
  public dataRepositoryConstructor: typeof TasksRepository;
  public dependencies: Dependencies;

  private selectedItemsData: Array<TaskItem> = [];
  private selectedStatuses: GridSelectionStatuses;

  constructor(component: TasksWebComponent) {
    super(component);
    this.initialSetup({
      viewConstructor: TasksView,
      viewTemplate: templateTasks,
      dataRepositoryConstructor: TasksRepository,
    });
  }

  async getDataForView() {
    return this.dataRepository.getDataForView();
  }

  private toggleDeleteButton() {
    let disabled = false;

    // loop through grid selected items in order to determine availability of delete button
    for (const element of this.selectedItemsData) {
      const task: TaskItem = element;

      // check if the user is the task owner
      if (task.ownerId !== this.dependencies.getCurrentUser()._id) {
        disabled = true;
        break;
      }
    }

    this.view.dom.$deleteBtn.disabled = disabled;
  }

  private toggleRetryButton() {
    let disabled = false;

    // loop through grid selected items in order to determine availability of restart button
    for (const element of this.selectedItemsData) {
      const task: TaskItem = element;

      // check if current user is not the task owner
      if (task.ownerId !== this.dependencies.getCurrentUser()._id) {
        disabled = true;
        break;
      }

      // check if task type is not allowed retryable tasks
      if (!RetryTasks.includes(task.type) || task.status !== TaskStatus.STATUS_PROCESSED) {
        disabled = true;
        break;
      }

      // check if scheduled
      if (task.specifics?.scheduler?.scheduledDeploy) {
        disabled = true;
        break;
      }

      // check if task has failed subtasks
      if (!task.hasFailedTasks) {
        disabled = true;
        break;
      }
    }

    this.view.dom.$retryBtn.disabled = disabled;
  }

  public onSelectionChanged(ev: WebComponentEvent) {
    this.selectedItemsData = ev.detail.selectedItemsData;

    if (this.selectedItemsData.length === 0) {
      this.view.dom.$deleteBtn.disabled = true;
      this.view.dom.$retryBtn.disabled = true;
      return;
    }

    this.toggleDeleteButton();
    this.toggleRetryButton();
  }

  public async onRetryButtonClick(ev: WebComponentEvent) {
    this.showRetryConfirmationDialog(this.onRetryButtonConfirmationDialogConfirm.bind(this));
  }

  private showRetryConfirmationDialog(confirmCallback) {
    const windowTitle = this.dependencies.translateFn('Restart selected tasks');
    const windowBody = this.dependencies.translateFn('Are you sure you want to restart the selected tasks?');
    const windowFooter = this.view.getRetryConfirmationDialogFooterHtml();

    GzDialogWebComponent.computeCustomDialog(windowBody, windowFooter, windowTitle, null, confirmCallback);
  }

  private async onRetryButtonConfirmationDialogConfirm(ev: WebComponentEvent) {
    // try to perform request
    const unknownErrorMessage = this.dependencies.translateFn('Tasks not restarted. Please retry.');
    let removeError = null;
    try {
      const response = await this.dataRepository.retryRecords(this.selectedItemsData);

      if (!response?.success) {
        removeError = unknownErrorMessage;
      }
    } catch (error) {
      removeError = error.message || unknownErrorMessage;
    }

    // when error, then display
    if (removeError) {
      showErrorToast(removeError);
      return;
    }

    showSuccessToast(this.dependencies.translateFn('Tasks restarted successfully'));

    this.refreshGrid();
  }

  public async onDeleteButtonClick(ev: WebComponentEvent) {
    this.showDeleteConfirmationDialog(this.onDeleteButtonConfirmationDialogConfirm.bind(this));
  }

  private showDeleteConfirmationDialog(confirmCallback) {
    const windowStrings = this.view.getDeleteConfirmationDialogWindowStrings(this.getSelectionStatus());

    const windowTitle = windowStrings.title;
    const windowBody = windowStrings.message;
    const windowFooter = this.view.getDeleteConfirmationDialogFooterHtml();

    GzDialogWebComponent.computeCustomDialog(windowBody, windowFooter, windowTitle, null, confirmCallback);
  }

  private async onDeleteButtonConfirmationDialogConfirm(ev: WebComponentEvent) {
    // try to perform request
    const unknownErrorMessage = this.dependencies.translateFn('Tasks were not deleted. Please retry.');
    let removeError = null;
    try {
      const response = await this.dataRepository.removeRecords(this.selectedItemsData);

      if (!response) {
        removeError = unknownErrorMessage;
      }
    } catch (error) {
      removeError = error.message || unknownErrorMessage;
    }

    // when error, then display
    if (removeError) {
      showErrorToast(removeError);
      return;
    }

    const successMessage = this.dependencies.translateFn('Tasks deleted successfully');
    showSuccessToast(successMessage);

    this.refreshGrid();
  }

  private resetSelectedStatuses() {
    this.selectedStatuses = {
      [TaskStatus.STATUS_PENDING]: 0,
      [TaskStatus.STATUS_PROCESSING]: 0,
      [TaskStatus.STATUS_PROCESSED]: 0,
    };
  }

  private getSelectionStatus(): TaskSelectionStatus {
    this.resetSelectedStatuses();

    // count how many tasks have each status
    this.selectedItemsData.forEach(task => {
      this.selectedStatuses[task.status]++;
    });

    const pendingCount = this.selectedStatuses[TaskStatus.STATUS_PENDING];
    const inProgressCount = this.selectedStatuses[TaskStatus.STATUS_PROCESSING];
    const finishedCount = this.selectedStatuses[TaskStatus.STATUS_PROCESSED];

    switch (true) {
      // single selection
      case this.selectedItemsData.length === 1 && pendingCount === 1:
        return TaskSelectionStatus.SINGLE_PENDING;
      case this.selectedItemsData.length === 1 && inProgressCount === 1:
        return TaskSelectionStatus.SINGLE_IN_PROGRESS;
      case this.selectedItemsData.length === 1 && finishedCount === 1:
        return TaskSelectionStatus.SINGLE_FINISHED;

      // multiple tasks selected
      case pendingCount > 0 && inProgressCount > 0 && finishedCount > 0:
        return TaskSelectionStatus.ALL_STATUSES;
      case pendingCount > 0 && inProgressCount > 0:
        return TaskSelectionStatus.PENDING_AND_IN_PROGRESS;
      case pendingCount > 0 && finishedCount > 0:
        return TaskSelectionStatus.PENDING_AND_FINISHED;
      case inProgressCount > 0 && finishedCount > 0:
        return TaskSelectionStatus.FINISHED_AND_IN_PROGRESS;
      case pendingCount > 1:
        return TaskSelectionStatus.ALL_PENDING;
      case inProgressCount > 1:
        return TaskSelectionStatus.ALL_IN_PROGRESS;
      default:
        return TaskSelectionStatus.ALL_FINISHED;
    }

  }


  private refreshGrid() {
    this.view.dom.$grid.controller?.handleGridItemsLoad(true);
  }

  public computeEventListeners(): IListenerConfig[] {
    return [
      {
        element: this.view.dom.$grid,
        event: TasksGridEvents.CLICK_ON_REPORT_ICON,
        callback: this.onReportCellIconClick,
      },
      {
        element: this.view.dom.$grid,
        event: GridEvents.SELECT_ROWS,
        callback: this.onSelectionChanged,
      },
      {
        element: this.view.dom.$retryBtn,
        event: 'click',
        callback: this.onRetryButtonClick,
      },
      {
        element: this.view.dom.$deleteBtn,
        event: 'click',
        callback: this.onDeleteButtonClick,
      },
      {
        element: this.view.dom.$grid,
        event: TasksGridEvents.CLICK_ON_STATUS,
        callback: this.onStatusCellTextClick,
      },
      {
        element: this.view.dom.$subTasks,
        event: 'backButtonClick',
        callback: this.navigateBack,
      },
    ];
  }

  public onReportCellIconClick(event: CustomEvent) {
    let recordSource = event?.detail.rowData.item.recordSource;
    if (recordSource === DataProviderType.ENDPOINTS) {
      if (this.dependencies.isServiceAvailable(DataProviderType.EPS)) {
        recordSource = DataProviderType.EPS;
      } else if (this.dependencies.isServiceAvailable(DataProviderType.SVE)) {
        recordSource = DataProviderType.SVE;
      }
    }

    const report = {
      name: this.dependencies.translateFn('Report generated for task {TASK_NAME}', {
        TASK_NAME: getTaskTypeLabel(event?.detail.rowData.item.type, this.dependencies.translateFn),
      }),
      type: event?.detail.rowData.item.reportType,
      recordSource: recordSource,
      targets: event?.detail.rowData.item.targets,
      options: { taskId: event?.detail.rowData.item._id },
      scheduleInfo: {
        type: ReportScheduleType.SCHEDULE_TYPE_TASK,
        recurrence: ReportRecurrenceType.RECURRENCE_TYPE_TASK,
      },
    };

    this.dependencies.generateReport(report);
  }

  private onStatusCellTextClick(event: CustomEvent) {
    this.view.showSubTasksPage({
      id: event.detail.rowData.item._id,
      name: event.detail.rowData.item.taskName,
      type: event.detail.rowData.item.type,
      hasContainers: event.detail.rowData.item.hasContainers,
      specifics: event.detail.rowData.item.specifics,
    });
    this.component.dispatchEvent(
      new CustomEvent(TasksGridEvents.SHOW_SUB_TASKS_PAGE, {
        bubbles: true,
        composed: true,
      })
    );
  }

  private navigateBack() {
    this.view.hideSubTasksPage();
    this.component.dispatchEvent(
      new CustomEvent(TasksGridEvents.HIDE_SUB_TASKS_PAGE, {
        bubbles: true,
        composed: true,
      })
    );
    this.refreshGrid();
  }
}
